import { galText } from "./accounts";
import type { Load, Route, RouteDay, RouteStop, StopTank } from "./api";
import { MAX_GAL, type Gallons, gallonsText, parseGallons } from "./pickup";
import { changeRefusalText, entryRefusalText } from "./offline/messages";
import { type OutboxEntry, STATE_TEXT } from "./offline/outbox";

export const SKIP_REASONS = ["Closed", "Nothing to collect", "Customer asked to skip", "Other"] as const;
export type SkipChoice = (typeof SKIP_REASONS)[number];

/** The results of a stop other than Done (rules D-7, D-16), in the order of their buttons. */
export type Outcome = "skipped" | "measured" | "partial" | "no_access";
export const OUTCOME_ORDER: Outcome[] = ["skipped", "measured", "partial", "no_access"];

interface OutcomeText {
  button: string;
  why: string;
  question: string;
  reasons: readonly string[];
  confirm: string;
  saved: string;
  reads: boolean;
}

export const OUTCOMES: Record<Outcome, OutcomeText> = {
  skipped: { button: "Skip", why: "why you skip the stop", question: "Why did you skip the stop?", reasons: SKIP_REASONS, confirm: "Skip the stop", saved: "skipped", reads: false },
  measured: {
    button: "Measured",
    why: "why the container is not emptied",
    question: "Why is the container not emptied?",
    reasons: ["Construction", "A vehicle blocks the container", "Other"],
    confirm: "Save: measured",
    saved: "measured, not emptied",
    reads: true,
  },
  partial: {
    button: "Partly emptied",
    why: "why the container is emptied in part",
    question: "Why is the container only partly emptied?",
    reasons: ["Frozen waste", "The truck is full"],
    confirm: "Save: partly emptied",
    saved: "partly emptied",
    reads: true,
  },
  no_access: {
    button: "No access",
    why: "why you cannot get to the container",
    question: "Why can you not get to the container?",
    reasons: ["Outside the open hours", "Locked", "Other"],
    confirm: "Save: no access",
    saved: "not reached (no access)",
    reads: false,
  },
};

/** The codes that the API keeps for a partly emptied tank. */
export const PARTIAL_CODES: Record<string, "frozen" | "truck_full"> = { "Frozen waste": "frozen", "The truck is full": "truck_full" };
export const PARTIAL_TEXT: Record<string, string> = { frozen: "Frozen waste", truck_full: "The truck is full" };

/** The open load of the driver for this truck and day: no result, no approval, the highest number. */
export function currentLoad(loads: readonly Load[], truck: string, day: string): Load | undefined {
  return loads
    .filter((load) => load.mine && load.day === day && load.truck === truck && load.can_edit && load.result === null)
    .sort((a, b) => b.number - a.number)[0];
}

/** The open load of the user for the truck and the day. With none, a new load when the user may make one. */
export async function ensureLoad(
  loads: readonly Load[],
  canMake: boolean,
  truck: string,
  day: string,
  make: (entry: { day: string; truck: string }) => Promise<Load | null>,
): Promise<Load | undefined> {
  const open = currentLoad(loads, truck, day);
  if (open || !canMake) return open;
  return (await make({ day, truck })) ?? undefined;
}

export const netText = (gallons: Gallons) => `Net ${galText(Math.max(0, gallons.raw - gallons.scrap))}`;

export function progressText(route: Pick<Route, "stops">): string {
  const visited = route.stops.filter((stop) => stop.state !== "planned").length;
  return `${visited} of ${route.stops.length} ${route.stops.length === 1 ? "stop" : "stops"} visited`;
}

/** The first planned stop, which the page opens. */
export const nextStop = (route: Pick<Route, "stops">): RouteStop | undefined => route.stops.find((stop) => stop.state === "planned");

/** After a saved stop collapses: the heading to scroll to the top, and the focus (next open stop, else the stop list). */
export function afterSave(route: Pick<Route, "id" | "stops">, savedId: string, changed = false): { scrollId: string | null; focusId: string } {
  if (changed) return { scrollId: `stop-${savedId}`, focusId: `change-${savedId}` };
  const at = route.stops.findIndex((stop) => stop.id === savedId);
  const open = (stop: RouteStop) => stop.state === "planned" && stop.id !== savedId;
  const after = route.stops.slice(at + 1).find(open);
  if (after) return { scrollId: `stop-${savedId}`, focusId: `stop-${after.id}` };
  const before = route.stops.find(open);
  return before ? { scrollId: `stop-${before.id}`, focusId: `stop-${before.id}` } : { scrollId: `stop-${savedId}`, focusId: `stops-${route.id}` };
}

/** A tel: link for a phone with 7 or more digits, else null. */
export function telHref(phone: string): string | null {
  const digits = phone.replace(/[^\d+]/g, "");
  return digits.replace(/\D/g, "").length >= 7 ? `tel:${digits}` : null;
}

/** The reason of a stop result to save, or an error text. A partly emptied tank saves its code. */
export function outcomeReason(outcome: Outcome, choice: string, other: string): { reason: string } | { error: string } {
  const why = OUTCOMES[outcome].why;
  if (!OUTCOMES[outcome].reasons.includes(choice)) return { error: `Choose ${why}.` };
  if (outcome === "partial") return { reason: PARTIAL_CODES[choice] };
  if (choice !== "Other") return { reason: choice };
  const text = other.trim();
  if (!text) return { error: `Enter ${why}.` };
  if (text.length > 500) return { error: "The reason can have 500 characters at most." };
  return { reason: text };
}

/** The skip reason to save, or an error text. */
export const skipReason = (choice: SkipChoice | "", other: string) => outcomeReason("skipped", choice, other);

/** Rule D-17: a partly emptied stop needs the total and scrap gallons pumped, unless its pickup is saved. */
export function pumpedGallons(total: string, scrap: string, saved: boolean): Gallons | null | { at: "gallons" | "scrap"; error: string } {
  if (saved) return null;
  if (!total.trim()) return { at: "gallons", error: "Type the total gallons that you pumped." };
  const gallons = parseGallons(total, scrap);
  return "error" in gallons ? { at: gallons.field === "total" ? "gallons" : "scrap", error: gallons.error } : gallons;
}

/** Rule D-16: the gallons left in the container, from the ruler, or an error text. */
export function parseLeft(text: string): number | string {
  const value = text.trim();
  if (!value) return "Enter the gallons left in the container, from the ruler.";
  if (!/^\d+(?:\.\d+)?$/.test(value)) return "Type the gallons left as a number, for example 300.";
  const gallons = Number(value);
  if (gallons > MAX_GAL) return `The gallons left cannot be more than ${MAX_GAL.toLocaleString("en-US")}.`;
  return gallons;
}

export type OutcomeField = "left" | "reason" | "other" | "gallons" | "scrap";
export interface OutcomeEntry {
  choice: string;
  other: string;
  left: string;
  text: string;
  scrap: string;
  pickupSaved: boolean;
}
export type OutcomeInput = { reason: string; left: number | null; taken: Gallons | null } | { at: OutcomeField; error: string };

/** The values of an outcome form, or its first wrong field from the top and the error text. */
export function outcomeInput(outcome: Outcome, entry: OutcomeEntry): OutcomeInput {
  const left = OUTCOMES[outcome].reads ? parseLeft(entry.left) : null;
  if (typeof left === "string") return { at: "left", error: left };
  const reason = outcomeReason(outcome, entry.choice, entry.other);
  if ("error" in reason) return { at: OUTCOMES[outcome].reasons.includes(entry.choice) ? "other" : "reason", error: reason.error };
  const taken = outcome === "partial" ? pumpedGallons(entry.text, entry.scrap, entry.pickupSaved) : null;
  if (taken && "error" in taken) return taken;
  return { reason: reason.reason, left, taken };
}

/** A 409 from a stop save: another tab or device recorded the stop, so the page loads the day again. */
export const reloadsDay = (status: number) => status === 409;

const RESULT_TEXT: Partial<Record<RouteStop["state"], string>> = { skipped: "Skipped", measured: "Measured", partial: "Partly emptied", no_access: "No access" };
type Visited = Pick<RouteStop, "state" | "reason" | "outcome_reason" | "gallons_left">;

/** The gallons left and the reason of a visited stop, for example "300 gal left (Construction)". */
export function outcomeDetail(stop: Visited): string {
  const left = stop.gallons_left != null ? `${galText(stop.gallons_left)} left` : "";
  const why = stop.state === "partial" ? (PARTIAL_TEXT[stop.outcome_reason ?? ""] ?? "") : stop.reason;
  return [left, why && left ? `(${why})` : why].filter(Boolean).join(" ");
}

/** The result of a visited stop in words, for example "Measured: 300 gal left (Construction)". */
export function outcomeText(stop: Visited): string {
  const label = RESULT_TEXT[stop.state];
  if (!label) return "";
  const detail = outcomeDetail(stop);
  return detail ? `${label}: ${detail}` : label;
}

export function windowText(window: RouteStop["window"]): string {
  return window[0] && window[1] ? `${window[0]}–${window[1]}` : "Any time";
}

/** The plan of a stop after its window, for example " · planned arrival 08:00 · about 101 gal". */
export function plannedText(stop: Pick<RouteStop, "arrive" | "planned_gal">): string {
  const parts = [stop.arrive ? `planned arrival ${stop.arrive}` : "", stop.planned_gal != null ? `about ${galText(Math.round(stop.planned_gal))}` : ""];
  return parts.filter(Boolean).map((part) => ` · ${part}`).join("");
}

export interface KeptEntry {
  text: string;
  scrap?: string;
  pickupId: string;
  pickupSaved: boolean;
}

/** The typed entries that must outlive a page change: some text, or a saved pickup. */
export function entriesToKeep(entries: Record<string, KeptEntry>): Record<string, KeptEntry> {
  const kept: Record<string, KeptEntry> = {};
  for (const [id, { text, scrap = "", pickupId, pickupSaved }] of Object.entries(entries)) {
    if (text.trim() !== "" || scrap.trim() !== "" || pickupSaved) kept[id] = { text, scrap, pickupId, pickupSaved };
  }
  return kept;
}

/** The kept entries from session storage; bad or old data gives none. */
export function keptEntries(raw: string | null): Record<string, KeptEntry> {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw ?? "{}");
  } catch {
    return {};
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
  const kept: Record<string, KeptEntry> = {};
  for (const [id, value] of Object.entries(parsed as Record<string, unknown>)) {
    const entry = value as Partial<KeptEntry> | null;
    if (entry && typeof entry.text === "string" && typeof entry.pickupId === "string" && typeof entry.pickupSaved === "boolean") {
      kept[id] = { text: entry.text, scrap: typeof entry.scrap === "string" ? entry.scrap : "", pickupId: entry.pickupId, pickupSaved: entry.pickupSaved };
    }
  }
  return kept;
}

/** One "not saved" text: the save helpers already say "was not saved" for a network failure. */
export function notSavedText(text: string): string {
  return /not saved/i.test(text) ? text.replace(/^.*? was not saved\. /, "Not saved. ") : `Not saved. ${text}`;
}

/** The entries of the stops that are still planned on these routes; the others are stale. */
export function plannedEntries<T>(entries: Record<string, T>, routes: readonly Route[]): Record<string, T> {
  const open = new Set(routes.flatMap((route) => route.stops.filter((stop) => stop.state === "planned").map((stop) => stop.id)));
  return Object.fromEntries(Object.entries(entries).filter(([id]) => open.has(id)));
}

/** Why the stops of a route stay closed for a member who cannot do its pre-trip check. */
export function checkWaitingText(mine: boolean): string {
  return mine
    ? "The stops open after the pre-trip check. Your role cannot do the check. Ask an administrator to do it, or to give the route to a driver."
    : "The stops open after the pre-trip check. Only the driver of the route or an administrator can do it.";
}

/** The day after a new tank pin, or none: each stop at the account takes it. */
export function withTankPin(day: RouteDay, accountId: string, tank: StopTank | null): RouteDay {
  return {
    ...day,
    routes: day.routes.map((route) => ({
      ...route,
      stops: route.stops.map((stop) => (stop.account_id === accountId ? { ...stop, tank: tank ? { ...tank } : null } : stop)),
    })),
  };
}

type DayOf = Pick<RouteDay, "day" | "today">;

/** True when the route day is over on the server or on the phone clock. */
export const routeDayOver = (day: DayOf, clockDay: string) => day.day < day.today || day.day < clockDay;

/** Rule SO-1033: the driver changes a saved stop result until the end of the route day, also on a finished route. */
export const canChangeStop = (day: DayOf, route: Pick<Route, "status">, stop: Pick<RouteStop, "state">, clockDay: string) =>
  stop.state !== "planned" && route.status !== "cancelled" && day.day === day.today && day.day === clockDay;

/** The line of a saved stop after its route day (SO-1036). */
export const ASK_OFFICE = "The route day is over. To change this entry, ask the office.";

/** The line over the stop form while the driver changes a saved result. */
export const CHANGING_TEXT = "Change the saved entry, then save it again. The office sees each change.";

const CHANGE_WORD: Record<Outcome, string> = { skipped: "skipped", measured: "measured", partial: "partly emptied", no_access: "no access" };

/** The save button of a change: one word pattern for each outcome (SO-1058). */
export const changeButton = (outcome: Outcome | null) => `Save the change: ${outcome ? CHANGE_WORD[outcome] : "done"}`;

type KeptOf = Pick<OutboxEntry, "state" | "reason" | "body"> & Partial<Pick<OutboxEntry, "kind">>;

/** The outcome of a kept stop entry or change; null is done. */
export const entryOutcome = (entry: Pick<OutboxEntry, "body">): Outcome | null => {
  const state = typeof entry.body.state === "string" ? entry.body.state : "";
  return state in OUTCOMES ? (state as Outcome) : null;
};

export const isChange = (entry: Pick<OutboxEntry, "body">) => entry.body.change_id !== undefined;

/** The top status line of a kept entry: a refused entry gets the text of its card (SO-1063, SO-1081). */
export function keptStatusText(text: string, entry: KeptOf | undefined, pickupSaved: string | null = null): string {
  if (entry?.state !== "failed") return `${STATE_TEXT[entry?.state === "sent" ? "sent" : "saved"]}: ${text}`;
  if (pickupSaved) return `Not saved: ${text} ${pickupSaved}`;
  const outcome = entryOutcome(entry);
  const reason = !entry.reason
    ? ""
    : isChange(entry)
      ? changeRefusalText(entry.reason, changeButton(outcome))
      : entry.kind === "stop"
        ? entryRefusalText(entry.reason, firstButton(outcome))
        : entry.reason;
  return `Not saved: ${text} ${reason}`.trim();
}

/** The save button of a first stop entry with no saved pickup. */
const firstButton = (outcome: Outcome | null) => (outcome ? OUTCOMES[outcome].confirm : "Save and mark done");

/** The server gets the change, then its result line comes into view (SO-1064). */
export const changeResultShown = (entry: Pick<OutboxEntry, "state"> | undefined) => entry?.state === "sent";

/** The saved values of a closed stop card, for example "50 gal total, 10 gal scrap" (SO-1059). */
export function savedValues(stop: Visited & Pick<RouteStop, "raw_gal" | "scrap_gal">): string {
  const pumped = stop.raw_gal != null ? gallonsText({ raw: stop.raw_gal, scrap: stop.scrap_gal ?? 0 }) : "";
  return [pumped, outcomeDetail(stop)].filter(Boolean).join(", ");
}

/** The words after "is" or "is changed:" in the result line of a closed stop. */
export function outcomeSaved(outcome: Outcome, reason: string, left: number | null, pumped: Gallons | null): string {
  const parts = [pumped ? gallonsText(pumped) : "", left !== null ? `${galText(left)} left` : ""].filter(Boolean);
  if (parts.length) return `${OUTCOMES[outcome].saved}, ${parts.join(", ")}`;
  return outcome === "skipped" ? `${OUTCOMES[outcome].saved} (${reason})` : OUTCOMES[outcome].saved;
}

export interface ChangeFields {
  text: string;
  scrap: string;
  outcome: Outcome | null;
  choice: string;
  other: string;
  left: string;
}

const gallonText = (value: number | null | undefined) => (value == null ? "" : String(value));

/** The stop form with the saved result of a stop, for a change (SO-1033). */
export function changeFields(stop: Pick<RouteStop, "state" | "reason" | "outcome_reason" | "gallons_left" | "raw_gal" | "scrap_gal">): ChangeFields {
  const outcome = stop.state === "done" || stop.state === "planned" ? null : stop.state;
  const reasons = outcome ? OUTCOMES[outcome].reasons : [];
  const named = outcome === "partial" ? (PARTIAL_TEXT[stop.outcome_reason ?? ""] ?? "") : stop.reason;
  const choice = !named ? "" : reasons.includes(named) ? named : reasons.includes("Other") ? "Other" : "";
  return {
    text: gallonText(stop.raw_gal),
    scrap: gallonText(stop.scrap_gal),
    outcome,
    choice,
    other: choice === "Other" && named !== "Other" ? named : "",
    left: gallonText(stop.gallons_left),
  };
}
